import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject, NgZone } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Tooltip } from 'bootstrap';
import type { Subscription } from 'rxjs';
import { interval } from 'rxjs';

import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { MegasService } from '../services/megas.service';
import { PassDataService } from '../services/pass-data.service';

@Component({
  standalone: false,
  selector: 'invd-validate-pools',
  templateUrl: './validate-pools.component.html',
  styleUrls: ['./validate-pools.component.scss'],
})
export class ValidatePoolsComponent implements OnInit, AfterViewInit, OnDestroy {
  public isAllMegasHaveErrors = false;
  public dispalyCancelPopupWindow = false;
  public isAllowToNavigateAway = false;
  isContinueButtonDisabled = true;
  detectedMegasFromPool = 0;
  countForValidatedMegas = 0;
  countForSuccessMegas = 0;
  countForErrorsMegas = 0;
  validatedMegaList: any;
  displayCancelErrorMessage = '';
  response: any;
  timer: any;
  validationStatusSubscription: Subscription;
  megaValidationStatus: string[] = [];
  poolProgressCount: string[] = [];
  poolProgressCountLength: number[] = [];
  displayNavigateAwayPopup = false;
  destinationUrl = '/';
  isUploadCancelledFromValidatePools = true;
  navSubscription: Subscription;
  poolFailureCount: number[] = [];
  public errorReportResponse: any;
  public isErrorReportDownloaded = false;
  public downloadReportSubscription: Subscription;
  public isYesButtonClickedToCancelUpload = false;
  public downloadErrorReportLinkClicked = true;

  private router = inject(Router);
  private passDataService = inject(PassDataService);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private megaService = inject(MegasService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.navigationController();
    this.timer = interval(7000);

    this.downloadErrorReportLinkClicked = true;
    this.validatedMegaList = this.passDataService.getValidatedMegaList();
    this.validatedMegaList = this.validatedMegaList && this.validatedMegaList.detectedMegas;
    if (this.validatedMegaList != null) {
      this.detectedMegasFromPool = this.validatedMegaList.length;

      this.validatedMegaList.forEach((item) => {
        this.poolProgressCountLength.push(item.poolCount.length);
      });
    }
    this.validationStatusSubscription = this.timer.subscribe(() => {
      this.getValidationStatus();
    });
  }

  ngAfterViewInit() {
    this.onInitTooltipError();
    this.navigationController();
  }

  ngOnDestroy() {
    if (this.validationStatusSubscription) {
      this.validationStatusSubscription.unsubscribe();
    }
    this.navSubscription.unsubscribe();
    if (this.downloadReportSubscription) {
      this.downloadReportSubscription.unsubscribe();
    }
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  public navigationController() {
    this.navSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('allocate-pools') > 0) {
          this.isAllowToNavigateAway = true;
          this.displayNavigateAwayPopup = false;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('upload-pool-info') > 0 && this.isErrorReportDownloaded) {
          this.isAllowToNavigateAway = true;
          this.displayNavigateAwayPopup = false;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('upload-pool-info') > 0) {
          this.isAllowToNavigateAway = false;
          this.displayNavigateAwayPopup = true;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('error') > 0) {
          this.isAllowToNavigateAway = true;
        } else {
          this.displayNavigateAwayPopup = true;
          this.isAllowToNavigateAway = false;
          this.destinationUrl = event.url;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  public cancelUpload(): void {
    this.isYesButtonClickedToCancelUpload = true;
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    this.megaService.cancelUpload(request).subscribe({
      next: (data) => this.handelCancelUploadSuccess(data),
      error: (error) => this.handleCancelUploadFail(error),
    });
  }

  public handelCancelUploadSuccess(response: any) {
    this.displayCancelErrorMessage = response.message;
    this.passDataService.setCancelErrorMessageFromValidatePool(this.displayCancelErrorMessage);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    if (this.isUploadCancelledFromValidatePools) {
      this.router.navigate(['/mega-upload/upload-pool-info']);
    }
    this.isAllowToNavigateAway = true;
    this.cdr.markForCheck();
  }

  public handleCancelUploadFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  handleCancelUpload(): void {
    this.dispalyCancelPopupWindow = true;
    this.passDataService.setIsUploadCancelledFromValidatePoolsPage(true);
  }

  public getValidationStatus(): void {
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();

    this.megaService.validationStatus(request).subscribe({
      next: (data) => this.handleValidationStatusSuccess(data),
      error: (error) => this.handleValidationStatusMegaServiceFail(error),
    });
  }

  public handleValidationStatusSuccess(response) {
    this.ngZone.run(() => {
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.countForValidatedMegas = 0;
      this.countForSuccessMegas = 0;
      this.countForErrorsMegas = 0;
      const countOfMegaList = response.detectedMegas.length;
      this.megaValidationStatus = [];
      this.poolProgressCount = [];
      this.poolFailureCount = [];
      this.passDataService.setDetectedMegaList(response.detectedMegas);

      response.detectedMegas.forEach((item) => {
        if (
          item.validationStatus === StringConstants.MEGA_VALIDATE_POOL_VALIDATION_STATUS_COMPLETE
        ) {
          this.countForSuccessMegas++;
        } else if (
          item.validationStatus ===
            StringConstants.MEGA_VALIDATE_POOL_VALIDATION_STATUS_MEGA_ERROR ||
          item.validationStatus ===
            StringConstants.MEGA_VALIDATE_POOL_VALIDATION_STATUS_POOL_ERROR ||
          item.validationStatus === StringConstants.MEGA_VALIDATE_POOL_VALIDATION_STATUS_FILE_ERROR
        ) {
          this.countForErrorsMegas++;
          this.isAllMegasHaveErrors = this.countForErrorsMegas === countOfMegaList ? true : false;
        }
        this.countForValidatedMegas = this.countForSuccessMegas + this.countForErrorsMegas;
        this.passDataService.setTotalCountFromValidatePools(this.countForValidatedMegas);
        this.megaValidationStatus.push(item.validationStatus);
        this.poolProgressCount.push(item.poolProgressCount);
        this.poolFailureCount.push(item.poolFailureCount);
      });
      if (response.jobStatus === StringConstants.MEGA_VALIDATE_POOL_JOB_VALIDATION_STATUS) {
        if (this.validationStatusSubscription) {
          this.validationStatusSubscription.unsubscribe();
        }
        this.isContinueButtonDisabled = this.isAllMegasHaveErrors ? true : false;
      }
      this.cdr.markForCheck();
      this.onInitTooltipError();
    });
  }

  public handleValidationStatusMegaServiceFail(_error: any): void {
    if (this.validationStatusSubscription) {
      this.validationStatusSubscription.unsubscribe();
    }
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public navigatePageTo(pageURL) {
    this.validationStatusSubscription.unsubscribe();
    this.router.navigate([pageURL]);
    this.isAllowToNavigateAway = true;
    this.isUploadCancelledFromValidatePools = false;
    this.passDataService.setIsUploadCancelledFromValidatePoolsPage(false);
    this.cancelUpload();
  }

  private onInitTooltipError(): void {
    setTimeout(() => {
      this.initializeTooltips();
    }, 100);
  }

  private initializeTooltips(): void {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      if (!Tooltip.getInstance(tooltipTriggerEl)) {
        new Tooltip(tooltipTriggerEl, {
          customClass: 'fm-tooltip',
        });
      }
    });
  }

  navigateToAllocatePoolsPage() {
    this.router.navigate(['/mega-upload/upload-pool-info/allocate-pools']);
  }

  public downloadErrorReport(): void {
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    if (this.downloadErrorReportLinkClicked) {
      this.downloadReportSubscription = this.megaService
        .downloadErrorReport(request, { observe: 'response' })
        .subscribe({
          next: (data) => this.handledownloadErrorReportSuccess(data),
          error: (error) => this.handleDownloadErrorReportFail(error),
        });
    }

    this.downloadErrorReportLinkClicked = false;
  }

  public handledownloadErrorReportSuccess(errorReportResponse): void {
    this.isErrorReportDownloaded = true;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.errorReportResponse = errorReportResponse;
    let data = this.errorReportResponse.body;
    const contentDisposition = errorReportResponse.headers.get('content-disposition');
    const fileName =
      contentDisposition &&
      contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim().replace(/"/g, '');
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }
    this.downloadErrorReportLinkClicked = true;
    this.cdr.markForCheck();
    this.router.navigate(['/mega-upload/upload-pool-info']);
  }

  public handleDownloadErrorReportFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    if (this.downloadReportSubscription) {
      this.downloadReportSubscription.unsubscribe();
    }
    this.downloadErrorReportLinkClicked = true;
    this.cdr.markForCheck();
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
